import WizardButtonWrapper from '../../../Wizard/WizardButtonWrapper';
import { useState, useEffect } from 'react';
import { Channel } from 'types/channels';
import { ChannelList } from './ChannelList';
import { ChannelFormModal } from './ChannelFormModal';
import { CustomTemplate } from 'types/template';
import { validateChannelName } from '../../../../utils/validateChannelName';

interface Props {
  onModalClose: () => void;
  handleAddChannels: (channels: Channel[]) => void;
  selectedTemplate: CustomTemplate;
  handleFinishAddChannels: (channels: Channel[]) => void;
}

const defaultChannelValues = {
  displayName: '',
  description: '',
  isFavoriteByDefault: false,
  membershipType: 'standard' as const,
};

export default function AddChannels({
  onModalClose,
  handleAddChannels,
  handleFinishAddChannels,
  selectedTemplate,
}: Props) {
  const [channels, setChannels] = useState<Channel[]>(
    selectedTemplate.channels ?? []
  );
  const [newChannel, setNewChannel] = useState<Channel>(defaultChannelValues);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingChannelName, setEditingChannelName] = useState<string | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    handleAddChannels(channels);
  }, [channels, handleAddChannels]);

  const handleAddChannel = () => {
    const validationError = validateChannelName(
      newChannel.displayName,
      channels,
      editingChannelName
    );
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);

    const isEditingExistingChannel = editingChannelName !== null;
    if (isEditingExistingChannel) {
      setChannels(
        channels.map((channel) =>
          channel.displayName === editingChannelName
            ? { ...newChannel }
            : channel
        )
      );
      setEditingChannelName(null);
    } else {
      setChannels([...channels, { ...newChannel }]);
    }
    setNewChannel(defaultChannelValues);
    setShowAddModal(false);
  };

  const handleEditChannel = (channelName: string) => {
    const channelToEdit = channels.find(
      (channel) => channel.displayName === channelName
    );
    if (channelToEdit) {
      setEditingChannelName(channelName);
      setNewChannel({ ...channelToEdit });
      setShowAddModal(true);
      setError(null);
    }
  };

  const handleRemoveChannel = (channelName: string) =>
    setChannels(
      channels.filter((channel) => channel.displayName !== channelName)
    );

  const handleModalClose = () => {
    setShowAddModal(false);
    setNewChannel(defaultChannelValues);
    setEditingChannelName(null);
    setError(null);
  };

  return (
    <div>
      <ChannelList
        channels={channels}
        onEdit={handleEditChannel}
        onRemove={handleRemoveChannel}
        onAdd={() => setShowAddModal(true)}
      />

      <WizardButtonWrapper
        onModalClose={onModalClose}
        buttonProps={[
          {
            onClick: () => handleFinishAddChannels(channels),
            text: 'Next',
            isPrimary: true,
          },
        ]}
      />

      <ChannelFormModal
        show={showAddModal}
        channel={newChannel}
        isEditing={editingChannelName !== null}
        onClose={handleModalClose}
        onSave={handleAddChannel}
        onChange={setNewChannel}
        error={error}
      />
    </div>
  );
}
